import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useTheme } from '../../contexts/ThemeContext';

interface SessionTreeProps {
  rootSessionId: string;
  onSessionSelect?: (sessionId: string) => void;
  height?: string;
}

interface SessionNode {
  id: string;
  phase: string;
  status: string;
  created_at: string;
  parent_id: string | null;
  children: string[];
}

export const SessionTree: React.FC<SessionTreeProps> = ({
  rootSessionId,
  onSessionSelect,
  height = '400px'
}) => {
  const { darkMode } = useTheme();
  const [sessions, setSessions] = useState<Record<string, SessionNode>>({});
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  
  // Fetch session tree data
  useEffect(() => {
    const fetchSessionTree = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // This would be replaced with an actual API call
        const response = await fetch(`/api/sessions/${rootSessionId}/tree`);
        if (!response.ok) {
          throw new Error('Failed to fetch session tree');
        }
        
        const data = await response.json();
        setSessions(data.sessions);
        
        // Create nodes and edges
        const nodes: Node[] = [];
        const edges: Edge[] = [];
        
        Object.values(data.sessions).forEach((session: SessionNode) => {
          // Create node
          nodes.push({
            id: session.id,
            type: 'sessionNode',
            position: { x: 0, y: 0 }, // Will be positioned by layout
            data: {
              label: `Session ${session.id.substring(0, 8)}...`,
              phase: session.phase,
              status: session.status,
              created_at: session.created_at
            }
          });
          
          // Create edge from parent to this node
          if (session.parent_id) {
            edges.push({
              id: `e-${session.parent_id}-${session.id}`,
              source: session.parent_id,
              target: session.id,
              type: 'smoothStep'
            });
          }
        });
        
        setNodes(nodes);
        setEdges(edges);
        
        // Apply tree layout
        applyTreeLayout(nodes, edges, data.sessions);
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (rootSessionId) {
      fetchSessionTree();
    }
  }, [rootSessionId]);
  
  // Apply tree layout to nodes
  const applyTreeLayout = (nodes: Node[], edges: Edge[], sessions: Record<string, SessionNode>) => {
    // Find root node
    const rootNode = nodes.find(node => node.id === rootSessionId);
    if (!rootNode) return;
    
    // Position root node
    rootNode.position = { x: 0, y: 0 };
    
    // Helper function to position children
    const positionChildren = (nodeId: string, x: number, y: number, horizontalSpacing: number) => {
      const session = sessions[nodeId];
      if (!session) return;
      
      const children = session.children;
      if (children.length === 0) return;
      
      const childSpacing = horizontalSpacing / children.length;
      const startX = x - (horizontalSpacing / 2) + (childSpacing / 2);
      
      children.forEach((childId, index) => {
        const childNode = nodes.find(node => node.id === childId);
        if (childNode) {
          childNode.position = {
            x: startX + (index * childSpacing),
            y: y + 150
          };
          
          // Recursively position this child's children
          positionChildren(childId, childNode.position.x, childNode.position.y, childSpacing);
        }
      });
    };
    
    // Start positioning from root
    positionChildren(rootSessionId, 0, 0, 800);
    
    // Update nodes with new positions
    setNodes([...nodes]);
  };
  
  const handleNodeClick = (event: React.MouseEvent, node: Node) => {
    if (onSessionSelect) {
      onSessionSelect(node.id);
    } else {
      navigate(`/sessions/${node.id}`);
    }
  };
  
  if (isLoading) {
    return <div className={`flex justify-center items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{ height }}>Loading session tree...</div>;
  }
  
  if (error) {
    return <div className={`text-red-500 text-center ${darkMode ? 'text-red-400' : ''}`} style={{ height }}>Error: {error}</div>;
  }

  return (
    <div style={{ height }}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          fitView
          attributionPosition="bottom-right"
        >
          <Background color={darkMode ? '#555' : '#aaa'} />
          <Controls />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};
